import React, { Dispatch, SetStateAction } from 'react';
import {
  View, Text, StyleSheet, FlatList,
} from 'react-native';
import { FontAwesome } from '@expo/vector-icons';
import { useSelector } from 'react-redux';
import Colors from '../../../../constants/Colors';
import { AssetInstance, UserPermission } from '../../../../constants/Types';
import Style from '../../../../constants/Style';
import PressableStyled from '../../../../components/PressableStyled';
import ScreenEmptyState from '../../../../components/ScreenEmptyState';
import NoAssetsImage from '../../../../components/AssetManagement/NoAssetsImage';
import isMobile from '../../../../components/IsMobile';
import AssetListItem from './components/AssetListItem';
import SkeletonLoader from '../../../../components/SkeletonLoader';
import { AssetProperty } from '../ColumnSelectionPane';
import { AssetSortCategory } from '../../../../requests/Assets/GetAssets';

interface AssetListProps {
  assets: AssetInstance[] | null,
  setSelectedAsset: Dispatch<SetStateAction<AssetInstance>>,
  loading: boolean,
  count: number,
  pageSize: number,
  setPageSize: Dispatch<SetStateAction<number>>,
  sortDescendingSelected: boolean,
  setSortDescendingSelected: Dispatch<SetStateAction<boolean>>,
  loadNextPage: () => void,
  initialScrollIndex: number,
  setInitialScrollIndex: Dispatch<SetStateAction<number>>,
  imageShown: boolean,
  idShown: boolean,
  templateShown: boolean,
  locationShown: boolean,
  modelShown: boolean,
  manufacturerShown: boolean,
  serialShown: boolean,
  selectedSortCategory?: string,
  setSelectedSortCategory?: Dispatch<SetStateAction<string>>,
  searchValue: string,
}

const AssetList = ({
  assets, setSelectedAsset, loading, count, pageSize, setPageSize, sortDescendingSelected,
  loadNextPage, setSortDescendingSelected, initialScrollIndex, setInitialScrollIndex, imageShown,
  idShown, templateShown, locationShown, modelShown, manufacturerShown, serialShown, searchValue,
  selectedSortCategory, setSelectedSortCategory,
}: AssetListProps) => {
  const selectedBuildingId: string = useSelector((state:
    { selectedBuilding: { id: string } }) => state.selectedBuilding.id);
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canViewAssets: boolean = isAdmin || permissions?.includes(UserPermission.ASSETS_READ);

  const selectAsset = (a: AssetInstance, index: number) => {
    setSelectedAsset(a);
    setInitialScrollIndex(index);
  };

  const sort = (category: AssetSortCategory) => {
    if (selectedSortCategory === category) setSortDescendingSelected(!sortDescendingSelected);
    else {
      setSelectedSortCategory(category);
      setSortDescendingSelected(false);
    }
  };

  const SortableColumn = ({ name, category, testID }:
    { name: AssetProperty, category: AssetSortCategory, testID: string }) => {
    const sortEnabled: boolean = selectedSortCategory === category;
    const categorySortedDescending: boolean = sortEnabled && sortDescendingSelected;

    return <View style={styles.nameColumn}>
      <Text style={styles.headerText}>{name}</Text>
      <PressableStyled
        onPress={() => sort(category)}
        style={[styles.sortButton, sortEnabled && styles.sortButtonActive]}
        {...{ testID }}
      >
        <FontAwesome
          name={categorySortedDescending ? 'angle-up' : 'angle-down'}
          color={Colors.light.hereworksBlue500}
          size={16}
        />
      </PressableStyled>
    </View>;
  };

  return canViewAssets ? <View
    style={styles.container}
    testID='asset-mngm-assets-list'
  >
    {!isMobile && <View
      style={styles.header}
      testID='asset-table-header'
    >
      <SortableColumn
        name={AssetProperty.NAME}
        category={AssetSortCategory.NAME}
        testID='assets-sort-by-name-btn'
      />
      {idShown && <SortableColumn
        name={AssetProperty.ID}
        category={AssetSortCategory.ID}
        testID='assets-sort-by-id-btn'
      />}
      {templateShown && <SortableColumn
        name={AssetProperty.TEMPLATE}
        category={AssetSortCategory.TEMPLATE_NAME}
        testID='assets-sort-by-template-btn'
      />}
      {locationShown && <Text style={styles.headerText}>{AssetProperty.LOCATION}</Text>}
      {manufacturerShown && <Text style={styles.headerText}>{AssetProperty.MANUFACTURER}</Text>}
      {modelShown && <Text style={styles.headerText}>{AssetProperty.MODEL}</Text>}
      {serialShown && <Text style={styles.headerText}>{AssetProperty.SERIAL}</Text>}
    </View>}
    <FlatList
      getItemLayout={(_, index) => (
        { length: 80, offset: 80 * index, index }
      )}
      style={styles.list}
      data={assets}
      onLayout={({ nativeEvent: { layout: { height } } }) => {
        if (pageSize) return;
        setPageSize(Math.max(8, Math.ceil(height
          / (styles.listItem.height + styles.listItem.marginBottom))));
      }}
      onEndReached={loadNextPage}
      ListEmptyComponent={() => {
        if (loading || assets === undefined) {
          return <View style={styles.skeletonLoadContainer}>
            {Array.from(Array(pageSize || 8).keys())
              .map((_, index: number) => <SkeletonLoader
                key={`skeleton_${index}`}
                delay={index * 100}
              />)}
          </View>;
        }
        if (assets === null) {
          return <ScreenEmptyState
            image={<NoAssetsImage />}
            title='No assets found'
            subtitle='We were unable to load the list of assets at the moment'
          />;
        }
        if (searchValue) {
          return <ScreenEmptyState
            image={<NoAssetsImage />}
            title='No assets found'
            subtitle={`No assets were found matching the search term "${searchValue}"`}
          />;
        }
        return <ScreenEmptyState
          image={<NoAssetsImage />}
          title={selectedBuildingId ? 'No assets found' : 'No buildings available'}
          subtitle={selectedBuildingId
            ? 'Add an asset to the current building\n(Assets can only be added via the mobile app)'
            : 'There are no buildings in the system. A building is required for adding assets.'}
          buttonTitle=''
          buttonAction={() => { }}
          actionAvailable
        />;
      }}
      renderItem={({ item: asset, index }) => <AssetListItem
        {...{
          asset,
          imageShown,
          idShown,
          templateShown,
          locationShown,
          modelShown,
          manufacturerShown,
          serialShown,
        }}
        onPress={() => selectAsset(asset, index)}
        style={[styles.listItem, !isMobile && index % 2 === 1 && styles.listItemAlternate]}
      />}
      {...{ initialScrollIndex }}
      onScrollToIndexFailed={() => { }}
      keyExtractor={(_, index: number) => `asset_${index}`}
    />
    {(assets?.length > 0 && !!count) && <Text style={styles.listFooter}>
      {`1-${Math.min(assets.length, count)} of ${count} showing`}
    </Text>}
  </View> : <ScreenEmptyState
    title='No permission to view assets'
    subtitle='You do not have the required permission to view assets. Please contact your administrator.'
    image={<NoAssetsImage />}
  />;
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    ...Style.row,
    paddingVertical: 8,
    backgroundColor: Colors.light.hereworksBlue400,
    borderTopStartRadius: 8,
    borderTopEndRadius: 8,
  },
  headerText: {
    fontSize: 10,
    lineHeight: 16,
    fontFamily: 'Roboto_500Medium',
    color: '#FFF',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginEnd: 8,
    flex: 1,
  },
  nameColumn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
  },
  list: isMobile ? {
    paddingHorizontal: 16,
    marginHorizontal: -16,
  } : {
    flex: 1,
    borderWidth: 1,
    borderBottomStartRadius: 8,
    borderBottomEndRadius: 8,
    borderColor: Colors.light.hereworksBlue200,
  },
  listItem: {
    height: isMobile ? 144 : 60,
    marginBottom: isMobile ? 6 : 0,
  },
  listItemAlternate: {
    backgroundColor: Colors.light.hereworksWhite300,
  },
  listFooter: {
    fontSize: 14,
    lineHeight: 16,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
    alignSelf: 'stretch',
    textAlign: 'right',
    marginVertical: 12,
  },
  sortButton: {
    backgroundColor: Colors.light.hereworksBlue200,
    width: 20,
    height: 20,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sortButtonActive: {
    backgroundColor: Colors.light.hereworksBlue50,
  },
  skeletonLoadContainer: {
    marginHorizontal: isMobile ? 0 : 24,
  },
});

export default AssetList;
